import React, { useCallback, useEffect, useRef, useState } from "react";
import { sortBy } from "lodash";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { AddCircleOutline, Cancel } from "@mui/icons-material";
import { alpha, useTheme } from "@mui/material/styles";

import { HelpBox } from "@/components/atoms";
import { InsightResult, PenPortraitInsightVariable, SiteInfo } from "@/types";
import { blackOverlay, interpolateDivergant, toIconLabel, toIndex, toPercentage, whiteOverlay } from "@/utils";

interface MapSitesCardProps {
  title: string;
  variables: PenPortraitInsightVariable[];
  score: string;
  min: number;
  max: number;
  siteInfo: SiteInfo | null,
  onSiteHover: (site: number | null) => void;
  onSiteDelete: (site: number) => void;
}

const MapSitesCard: React.FC<MapSitesCardProps> = ({
  title,
  variables,
  score,
  min,
  max,
  siteInfo,
  onSiteHover,
  onSiteDelete,
}) => {
  const theme = useTheme();

  const [scores, setScores] = useState<PenPortraitInsightVariable[]>([]);
  const formatFunc = score === "rate" ? toPercentage : toIndex;

  const calcPercentage = (value: number) => {
    const mx = max ? max : 1;
    const mn = min ? min : 0;
    return Math.max(0.01, (value - mn) / (mx - mn));
  };

  useEffect(() => {
    let filtered = variables
      .filter((x) =>
        score === "rate"
          ? Number(x[score as keyof PenPortraitInsightVariable]) > 0
          : Number(x[score as keyof PenPortraitInsightVariable]) > 0,
      )
    setScores(sortBy(filtered, score).reverse());
  }, [score, variables]);

  const handleMouseEnter = (site: number) => {
    onSiteHover(site);
  }

  const handleMouseLeave = () => {
    onSiteHover(null);
  }

  if (scores.length === 0) {
    return null;
  } else {
    return (
      <Card
        key={title}
        sx={{
          borderRadius: 1,
          borderColor: theme.palette.divider,
          width: "100%",
          position: "relative",
          height: 560,
          overflowY: "auto"
        }}
      >
        <CardHeader
          title={title}
          slotProps={{ title: { variant: "body2" } }}
          sx={{ alignItems: "flex-start", pb: 0 }}
        />
        <CardContent sx={{ pb: 2, "&:last-child": { pb: 2 } }}>
          {scores.map((site, index) => {
            const value = Number(site[score as keyof PenPortraitInsightVariable])
              return (
                <Stack
                  key={index}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Stack
                    spacing={0}
                    px={1}
                    py={1}
                    mb={1}
                    onMouseEnter={() => handleMouseEnter(site.tag_id)}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                      borderRadius: "4px",
                      '&:hover': { backgroundColor: alpha(theme.palette.info.main, 0.051) },
                    }}
                    width="100%"
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {site.tag_name}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={3}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Box
                        height="4px"
                        borderRadius="4px"
                        width={`${calcPercentage(value) * 100}%`}
                        sx={{
                          backgroundColor: interpolateDivergant(
                            theme.palette.score.light,
                            theme.palette.score.main,
                            theme.palette.score.dark,
                            1 - calcPercentage(value),
                          ),
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: interpolateDivergant(
                            theme.palette.score.light,
                            theme.palette.score.main,
                            theme.palette.score.dark,
                            1 - calcPercentage(value),
                          ),
                        }}
                      >
                        {formatFunc(value)}
                      </Typography>
                    </Stack>
                  </Stack>
                  <IconButton
                    onClick={() => onSiteDelete(site.tag_id)}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </Stack>
              )
          })}
        </CardContent>
      </Card>
    );
  }
};

export default MapSitesCard;
